import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { UserEntity } from 'Models/Entities';
import { appConfigStore } from 'Models/AppConfigStore';
import CustomerSelection from 'Views/Components/_HumanWritten/CustomerSelection/CustomerSelection';
import { mapCustomerToWizardData } from 'Views/Components/_HumanWritten/CustomerSelection/CustomerSelectionHelpers';
import { observer, useLocalStore } from 'mobx-react';
import { getFerryBookingTransactionIdFromStorage } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { stringNotEmpty } from 'Util/TypeGuards';
import { useOptionalMessage } from 'Hooks/useOptionalMessage';
import { VehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { PassengerBookingWizardData } from '../../BookingWizardData/PassengerBookingWizardData';
import { PassengerTypeKey } from 'Models/Enums';
import { PassengerDetail } from '../../BookingWizardData/PassengerDetail';
import { CustomLocationState, isRegisterCustomerLocationState } from 'Models/_HumanWritten/LocationState';
import { useBookingWizardPricingCacheStore } from '../../BookingWizardPricingCacheStore';
import { WizardStepHeader } from '../Shared/WizardStepHeader';

export interface CustomerTabProps {
	locationState?: CustomLocationState;
}

function CustomerTab({ locationState }: CustomerTabProps) {
	const intl = useIntl();
	const fmtOptional = useOptionalMessage();
	const { wizardData, setWizardData, baseWizardErrors, setBaseWizardErrors } = useBookingWizardDataStore();

	const bookingPriceCache = useBookingWizardPricingCacheStore();

	const userId = useLocalStore<{ value: string }>(() => {
		// Check if the previous route was from RegisterCustomer component
		if (locationState && isRegisterCustomerLocationState(locationState) && locationState.userId) {
			if (wizardData.userId !== locationState.userId) {
				// onUpdateData is unnecessary because wizardData.userId can be updated without re-render
				wizardData.userId = locationState.userId;
				if (wizardData.innerWizardData.type === 'vehicle') {
					wizardData.innerWizardData.driverPhone = undefined;
					wizardData.innerWizardData.driverFirstName = undefined;
					wizardData.innerWizardData.driverLastName = undefined;
					wizardData.innerWizardData.cargoIdentification = undefined;
				}
			}
		}
		return { value: wizardData.userId };
	});

	// If the staff member is altering an existing booking or has already added bookings to the cart,
	// then they will no longer be able to change the customer details
	// (unless they remove all the bookings from the cart)
	const isAlteration = wizardData.wizardMode === 'ALTERATION';
	const hasCartBookings =
		getFerryBookingTransactionIdFromStorage() !== null &&
		Object.keys(bookingPriceCache.bookingPrices).filter(id => id !== '00000000-0000-0000-0000-000000000000')
			.length > 0;
	const isDisabled = (isAlteration || hasCartBookings) && stringNotEmpty(wizardData.userId);

	useEffect(() => {
		if (wizardData.userId !== userId.value) {
			// onUpdateData is unnecessary because wizardData.userId can be updated without re-render
			wizardData.userId = userId.value;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userId, userId.value]);

	let title: string;
	let subtitleDefault: string;
	if (isAlteration && stringNotEmpty(wizardData.userId)) {
		title = intl.formatMessage({
			id: 'customer-tab__title__alteration',
			defaultMessage: 'Booking customer',
		});
		subtitleDefault = 'Edits to this booking will be applied to the customer below.';
	} else if (hasCartBookings && stringNotEmpty(wizardData.userId)) {
		title = intl.formatMessage({
			id: 'customer-tab__title__cart-locked',
			defaultMessage: 'Booking customer',
		});
		subtitleDefault = 'Clear bookings from your cart to choose a different customer.';
	} else {
		title = intl.formatMessage({
			id: 'customer-tab__title',
			defaultMessage: 'Choose a customer',
		});
		subtitleDefault = 'Search for an existing customer or set up a new one.';
	}
	const subtitle = fmtOptional({
		id: isDisabled ? 'customer-tab__subtitle__disabled' : 'customer-tab__subtitle',
		defaultMessage: subtitleDefault,
	});

	return (
		<div className="booking-wizard-tab customer-selection customer-selection--curved">
			<WizardStepHeader
				iconName="person_search"
				title={title}
				subtitle={subtitle || undefined}
				showCloseLink={wizardData.wizardMode === 'ALTERATION'}
				bookingToEditId={wizardData?.bookingToEdit ?? ''}
			/>
			<div className="customer-selection__details-section booking-section-card">
				<CustomerSelection
					model={userId}
					modelProperty="value"
					backUrl="/booking-wizard/customer"
					disabled={isDisabled}
					phoneEnabled={appConfigStore.userPhoneEnabled}
					combobox={{
						errors: baseWizardErrors.userId,
						onAfterChange: (e, d) => {
							if (d.value !== '') {
								const newErrors = { ...baseWizardErrors };
								delete newErrors.userId;
								setBaseWizardErrors(newErrors);
							}
						},
					}}
					onSelect={(customer?: UserEntity) => {
						const newData = mapCustomerToWizardData(wizardData, customer);
						const isNewCustomer =
							wizardData.wizardMode === 'CREATE' && customer && customer.id !== wizardData.userId;

						if (isNewCustomer) {
							if (newData.innerWizardData instanceof VehicleBookingWizardData) {
								newData.innerWizardData.cargoIdentification = '';
								newData.innerWizardData.cargoTypeId = '';
								newData.innerWizardData.cargoMake = '';
								newData.innerWizardData.cargoModel = '';
								newData.innerWizardData.vehicleLengthId = '';
								newData.innerWizardData.vehicleWeightId = '';
								newData.innerWizardData.vehicleWidthId = '';
								newData.innerWizardData.trailerLengthId = '';
								newData.innerWizardData.trailerTypeId = 'NO_TRAILER';
								newData.innerWizardData.customerEmail = customer.email;
							}

							/*
								Reset the passenger DETAILS when the customer changes — a name, a
								phone number and a date of birth belong to whoever was selected
								before, and carrying them onto a different customer is how one
								person's details end up on another's booking.

								What is NOT a personal detail is what they bought. A fare type and a
								seat are chosen on the Tickets and Seats steps, before and after this
								one, and they belong to the booking rather than to the customer — so
								they are carried across the reset. Rebuilt from `key` and `driver`
								alone, choosing Premium on both legs and then picking a customer left
								the departing leg on the vessel's default (the seating step refills
								the leg it is showing) and the returning leg on nothing at all.
							*/
							if (
								newData.innerWizardData instanceof VehicleBookingWizardData ||
								newData.innerWizardData instanceof PassengerBookingWizardData
							) {
								const resetDetails = (key: PassengerTypeKey) =>
									(passenger: PassengerDetail) =>
										new PassengerDetail({
											key,
											driver: passenger.driver,
											// What was bought, on each leg.
											passengerClassId: passenger.passengerClassId,
											passengerClassName: passenger.passengerClassName,
											passengerClassColour: passenger.passengerClassColour,
											returningPassengerClassId: passenger.returningPassengerClassId,
											returningPassengerClassName: passenger.returningPassengerClassName,
											seatId: passenger.seatId,
											seatLabel: passenger.seatLabel,
											seatAutoAssigned: passenger.seatAutoAssigned,
											returningSeatId: passenger.returningSeatId,
											returningSeatLabel: passenger.returningSeatLabel,
											returningSeatAutoAssigned: passenger.returningSeatAutoAssigned,
										});

								const inner = newData.innerWizardData;
								inner.adultTickets = inner.adultTickets.map(resetDetails('A'));
								inner.childTickets = inner.childTickets.map(resetDetails('B'));
								inner.infantTickets = inner.infantTickets.map(resetDetails('C'));
								inner.passengerDTickets = inner.passengerDTickets?.map(resetDetails('D'));
								inner.passengerETickets = inner.passengerETickets?.map(resetDetails('E'));
								inner.passengerFTickets = inner.passengerFTickets?.map(resetDetails('F'));
								inner.passengerGTickets = inner.passengerGTickets?.map(resetDetails('G'));
								inner.passengerHTickets = inner.passengerHTickets?.map(resetDetails('H'));
							}
						}

						setWizardData(newData);
					}}
				/>
			</div>
		</div>
	);
}

export default observer(CustomerTab);
